import { useOpenWebPage } from '~/hooks/useOpenWebPage';
import { useToastMessages } from '~/hooks/useToastMessages';
import { useTranslation } from 'react-i18next';
import { Member } from '~/models/Member';
import { Post } from '~/models/Post';
import { Comment } from '~/models/Comment';
import { Photo } from '~/models/Photo';
import { IReportTarget, ReportReason, SupportRequestType, SupportService } from '~/services/domain/SupportService';
import { VERSION_WITH_BUILD } from '~/constants/version';
import { getDeviceDescription } from '~/utils/deviceDescription';
import { Platform } from 'react-native';
import { SUPPORT_URLS } from '~/constants/urls';

/**
 * Hook for submitting support tickets
 * Uses the SupportService for business logic and useOpenWebPage for URL handling
 */
export function useSupportService() {
  const openWebPage = useOpenWebPage();
  const { showErrorToast, showLoadingToast } = useToastMessages();
  const { t } = useTranslation();

  /**
   * Opens a general support ticket form in the browser
   * @param title The title for the support ticket
   */
  const submitTicket = (title: string) => {
    const url = SupportService.getTicketUrl(title);
    // Use external browser on Android to prevent crashes when uploading files
    const useExternalBrowser = Platform.OS === 'android';
    return openWebPage(url, useExternalBrowser);
  };

  /**
   * Records the support request on the server and opens the prefilled portal form it returns
   * @param requestType Report or Billing
   * @param target The reported member, post, comment or photo (none for billing)
   * @param reason Reason chosen by the member, reports only
   */
  const submitSupportRequest = async (requestType: SupportRequestType, target: IReportTarget, reason?: ReportReason) => {
    // Browsers block tabs opened after an await, so reserve the tab during the tap and point it at the form later.
    // A blocked tab can't be retried after the await either, so stop before recording anything.
    const pendingTab = Platform.OS === 'web' ? window.open('', '_blank') : null;
    if (Platform.OS === 'web' && !pendingTab) {
      showErrorToast(t('common.error.popupBlocked'));
      return;
    }

    // The reserved tab has focus on web, so the wait message goes there; native shows a toast
    let closeLoading: (() => void) | undefined;
    if (pendingTab) {
      try {
        pendingTab.document.title = t('common.openingForm');
        pendingTab.document.body.textContent = t('common.openingForm');
      } catch {
        // Some browsers don't expose the blank tab's document; it just stays blank until the form loads
      }
    } else {
      closeLoading = showLoadingToast(t('common.openingForm'));
    }

    let ticketUrl: string;
    try {
      const deviceInfo = getDeviceDescription();
      ({ TicketUrl: ticketUrl } = await SupportService.createSupportRequest(
        requestType,
        target,
        VERSION_WITH_BUILD,
        deviceInfo,
        reason
      ));
    } catch (error) {
      if (requestType === 'Billing') {
        // Billing support must stay reachable without our server: open the plain billing form
        ticketUrl = SUPPORT_URLS.atlassian.support.billingSupport;
      } else {
        closeLoading?.();
        pendingTab?.close();
        const status = (error as { status?: number } | undefined)?.status;
        const message =
          status === 429
            ? 'common.error.reportTooMany'
            : status === 404
              ? 'common.error.reportUnavailable'
              : 'common.error.reportFailed';
        showErrorToast(t(message), error);
        return;
      }
    }

    // Closed before opening: the iOS in-app browser only resolves once it's dismissed
    closeLoading?.();
    if (pendingTab) {
      pendingTab.location.href = ticketUrl;
      return;
    }

    // Use external browser on Android to prevent crashes when uploading files
    const useExternalBrowser = Platform.OS === 'android';
    return openWebPage(ticketUrl, useExternalBrowser);
  };

  /**
   * Opens a policy violation report form for a member
   * @param reportedMember The member being reported
   * @param reason Reason chosen by the member
   */
  const submitPolicyViolationForMember = (reportedMember: Member, reason: ReportReason) =>
    submitSupportRequest('Report', { MemberId: reportedMember.Id }, reason);

  /**
   * Opens a policy violation report form for a post
   * @param reportedPost The post being reported
   * @param reason Reason chosen by the member
   */
  const submitPolicyViolationForPost = (reportedPost: Post, reason: ReportReason) =>
    submitSupportRequest('Report', { PostId: reportedPost.Id }, reason);

  /**
   * Opens a policy violation report form for a comment
   * @param reportedComment The comment being reported
   * @param reason Reason chosen by the member
   */
  const submitPolicyViolationForComment = (reportedComment: Comment, reason: ReportReason) =>
    submitSupportRequest('Report', { CommentId: reportedComment.Id }, reason);

  /**
   * Opens a policy violation report form for a photo
   * @param reportedPhoto The photo being reported
   * @param reason Reason chosen by the member
   */
  const submitPolicyViolationForPhoto = (reportedPhoto: Photo, reason: ReportReason) =>
    submitSupportRequest('Report', { PhotoId: reportedPhoto.Id }, reason);

  /**
   * Opens a policy violation report form for a conversation the member is part of
   * @param messageId The conversation being reported
   * @param reason Reason chosen by the member
   */
  const submitPolicyViolationForConversation = (messageId: number, reason: ReportReason) =>
    submitSupportRequest('Report', { MessageId: messageId }, reason);

  /**
   * Opens a billing support request form
   */
  const submitBillingSupport = () => submitSupportRequest('Billing', {});

  return {
    submitTicket,
    submitPolicyViolationForMember,
    submitPolicyViolationForPost,
    submitPolicyViolationForComment,
    submitPolicyViolationForPhoto,
    submitPolicyViolationForConversation,
    submitBillingSupport
  };
}
